import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material'
import PropTypes from 'prop-types'
import { useDebouncedCallback } from 'use-debounce'

import { addItemToUpdateQueue } from '@/app/slices/quoteItemsSlice'
import TbxTooltip from '@/common/components/TbxTooltip/TbxTooltip'

const classes = {
    formControl_small: {
        width: '80px',
    },
    formControl_select: {
        minWidth: '100px',
    },
}

const CustomPropertySecProcess = ({
    isPartFromLibrary = false,
    onInputChange,
    process,
    quoteItemId,
    showLabel = true,
    sx = null,
}) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const [propertyValue, setPropertyValue] = useState(process.customValue || '')
    const [propertyError, setPropertyError] = useState(!process?.customValue)

    const { $type, ...rest } = process

    const debounceOnInputChange = useDebouncedCallback(onInputChange, 800)

    const handleInputChange = async (event) => {
        const value = event.target.value

        debounceOnInputChange.cancel()
        dispatch(addItemToUpdateQueue(quoteItemId))

        setPropertyError(value === '')
        setPropertyValue(value)

        if (value) {
            debounceOnInputChange({ ...rest, isEnabled: true, customValue: value })
        }
    }

    return (
        <TbxTooltip
            title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
            arrow
        >
            {process.customPropertyOptions?.length > 0 ? (
                <FormControl
                    color="primary"
                    error={propertyError}
                    size="small"
                    sx={[classes.formControl_select, ...(Array.isArray(sx) ? sx : [sx])]}
                    variant="outlined"
                >
                    {showLabel ? (
                        <InputLabel
                            htmlFor="select-customProperty"
                            shrink
                        >
                            {t(process.customPropertyName)}
                        </InputLabel>
                    ) : null}
                    <Select
                        data-testid="order-item-source"
                        disabled={isPartFromLibrary}
                        id="select-customProperty"
                        label={showLabel ? t(process.customPropertyName) : null}
                        labelId="select-customProperty-label"
                        sx={{
                            '& .MuiSelect-select': {
                                fontSize: '14px',
                                paddingBlock: '5px',
                                paddingInline: '12px',
                            },
                        }}
                        value={propertyValue}
                        displayEmpty
                        onChange={handleInputChange}
                    >
                        <MenuItem value="">
                            <span>{t('Select option')}</span>
                        </MenuItem>
                        {process.customPropertyOptions.map((option) => (
                            <MenuItem
                                key={option}
                                value={option}
                            >
                                {t(option)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            ) : (
                <TextField
                    disabled={isPartFromLibrary}
                    error={propertyError}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    inputProps={{
                        style: { fontSize: '14px', paddingBlock: '6px', paddingInline: '12px' },
                        'data-testid': 'order-item-secondary-process-input',
                    }}
                    label={showLabel ? process.customPropertyName : null}
                    size="small"
                    sx={[classes.formControl_small, ...(Array.isArray(sx) ? sx : [sx])]}
                    value={propertyValue}
                    variant="outlined"
                    onBlur={handleInputChange}
                    onChange={handleInputChange}
                />
            )}
        </TbxTooltip>
    )
}

CustomPropertySecProcess.propTypes = {
    isPartFromLibrary: PropTypes.bool,
    process: PropTypes.object,
    quoteItemId: PropTypes.string,
    showLabel: PropTypes.bool,
    sx: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])),
        PropTypes.func,
        PropTypes.object,
    ]),
    onInputChange: PropTypes.func,
}

export default CustomPropertySecProcess
